import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';
import {
    isLogicAppExpression,
    ReferenceAdapter,
    SharedModel,
    SqlAnalyticsEndpointDataset,
    SqlAnalyticsEndpointLinkedService,
    ValueAdapter
} from 'shell-models-lib';
import { DatasetAdapter } from './dataset.adapter';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SqlAnalyticsEndpointDataset),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            type: '*',
            // If remove this, the endpoint value can be changed in Fabric because through the UI input the dataset will be re-created and the raw can be reset
            // through the json edit the raw can be updated in "toModel"
            linkedService: '*',
            connectionSettings: '*',
            linkedServiceName: '*'
        }
    }
})
export class SqlAnalyticsEndpointDatasetAdapter extends DatasetAdapter<
    SqlAnalyticsEndpointDataset,
    Model.DatasetResource
> {
    public async postToModel(dataset: SqlAnalyticsEndpointDataset): Promise<void> {
        if (
            dataset.linkedService &&
            stringIsNullOrEmpty((dataset.linkedService as SqlAnalyticsEndpointLinkedService).endpoint) &&
            (dataset.extension._linkedService$ as Model.LinkedServiceResource).properties?.typeProperties?.endpoint
        ) {
            (dataset.linkedService as SqlAnalyticsEndpointLinkedService).endpoint = (
                dataset.extension._linkedService$ as Model.LinkedServiceResource
            ).properties.typeProperties.endpoint;
        }
    }

    protected async _toResource(dataset: SqlAnalyticsEndpointDataset): Promise<Model.DatasetResource> {
        const modelDatasetResource = (await super._toResource(dataset)) as Model.DatasetResource;
        const modelDataset = this.properties(modelDatasetResource) as Model.WebTableDataset;
        modelDataset.type = Metamodel.modelFor(SqlAnalyticsEndpointDataset).name;
        modelDataset.schema = this.columnValueToResource(dataset, dataset.columns);
        if (dataset.raw) {
            const parsed = JSON.parse(dataset.raw);
            _.merge(modelDatasetResource, parsed);
        }
        return modelDatasetResource;
    }

    protected async _toModel(
        modelDatasetResource: Model.DatasetResource,
        dataset?: SqlAnalyticsEndpointDataset
    ): Promise<SqlAnalyticsEndpointDataset> {
        if (!dataset) {
            dataset = SqlAnalyticsEndpointDataset.MODEL.create<SqlAnalyticsEndpointDataset>(modelDatasetResource.name);
        }
        await super._toModel(modelDatasetResource, dataset);

        dataset.columns = this.columnResourceToValue(dataset, modelDatasetResource.properties?.schema);
        dataset.columns.forEach((c) => {
            c.physicalType = c.columnType;
        });

        if (
            this.context.adapterConfig.tolerateInvalidReference &&
            modelDatasetResource.properties?.linkedServiceName &&
            !isLogicAppExpression(
                (modelDatasetResource.properties.linkedServiceName as SharedModel.LinkedServiceReference).referenceName
            )
        ) {
            const linkedServiceModel = SqlAnalyticsEndpointDataset.MODEL.attribute('linkedService').constraint.model;
            dataset.linkedService = ReferenceAdapter.toProxyModel(
                linkedServiceModel,
                modelDatasetResource.properties.linkedServiceName,
                this.context
            );
        }

        dataset.raw = JSON.stringify(
            await ValueAdapter.remainder(this.context, modelDatasetResource, SqlAnalyticsEndpointDataset.MODEL)
        );
        return dataset;
    }
}
